import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { comics, categories } from '@/mocks/comics';

const categoryToSlug: Record<string, string> = {
  '热血': 'rexue',
  '恋爱': 'lianai',
  '奇幻': 'qihuan',
  '武侠': 'wuxia',
  '都市': 'dushi',
  '悬疑': 'xuanyi',
  '职场': 'zhichang',
  '搞笑': 'gaoxiao',
  '恐怖': 'kongbu',
  '运动': 'yundong',
};

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === id);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center px-4">
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-3">漫画未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您查找的漫画不存在或已被移除。</p>
            <Link to="/" className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-2.5 rounded-full transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-arrow-left-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = comics.filter((c) => c.category === comic.category && c.id !== comic.id).slice(0, 4);

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩漫漫画网站`,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: comic.updatedAt,
    image: comic.cover,
    publisher: {
      '@type': 'Organization',
      name: '韩漫漫画网站',
    },
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.example.com/' },
      { '@type': 'ListItem', position: 2, name: `${comic.category}漫画`, item: `https://www.example.com/category/${categoryToSlug[comic.category] || 'rexue'}` },
      { '@type': 'ListItem', position: 3, name: comic.title },
    ],
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />

      <Navbar />
      <main className="pt-16">
        <section className="relative bg-gradient-to-br from-primary-800 via-primary-700 to-primary-900 py-12 md:py-20">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-10 right-10 w-60 h-60 rounded-full bg-accent-500/30 blur-3xl"></div>
            <div className="absolute bottom-10 left-20 w-40 h-40 rounded-full bg-accent-500/20 blur-3xl"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-2 text-xs text-accent-300 mb-6">
                <Link to="/" className="hover:text-background-50 transition-colors">首页</Link>
                <i className="ri-arrow-right-s-line"></i>
                <Link to={`/category/${categoryToSlug[comic.category] || 'rexue'}`} className="hover:text-background-50 transition-colors">{comic.category}漫画</Link>
                <i className="ri-arrow-right-s-line"></i>
                <span className="text-background-50/70">{comic.title}</span>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-1">
                  <div className="aspect-[3/4] rounded-xl overflow-hidden">
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                <div className="lg:col-span-2 flex flex-col justify-center">
                  <span className={`inline-flex self-start text-xs font-medium px-3 py-1 rounded-full mb-4 ${
                    comic.status === '连载中'
                      ? 'bg-accent-500/20 text-accent-200 border border-accent-400/30'
                      : 'bg-background-50/15 text-background-200 border border-background-300/30'
                  }`}>
                    {comic.status}
                  </span>
                  <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-background-50 mb-3">
                    {comic.title}
                  </h1>
                  <p className="text-sm text-background-200/80 mb-4">作者：{comic.author}</p>

                  <div className="flex items-center gap-6 mb-5">
                    <div className="flex items-center gap-1.5">
                      <i className="ri-star-fill text-yellow-400"></i>
                      <span className="text-background-50 font-semibold text-lg">{comic.rating}</span>
                      <span className="text-background-300 text-xs">分</span>
                    </div>
                    <div className="w-px h-6 bg-background-50/20"></div>
                    <div className="text-center">
                      <p className="font-heading text-xl font-bold text-background-50">{comic.chapters.length}+</p>
                      <p className="text-xs text-background-300">章节</p>
                    </div>
                    <div className="w-px h-6 bg-background-50/20"></div>
                    <div className="text-center">
                      <p className="font-heading text-xl font-bold text-background-50">{(comic.views / 10000).toFixed(0)}万</p>
                      <p className="text-xs text-background-300">阅读量</p>
                    </div>
                  </div>

                  <p className="text-sm text-background-200/80 leading-relaxed mb-6 max-w-lg">
                    {comic.description}
                  </p>

                  <Link
                    to={`/comic/${comic.id}`}
                    className="inline-flex items-center gap-2 bg-background-50 hover:bg-background-100 text-primary-700 font-medium px-7 py-3 rounded-full transition-colors whitespace-nowrap cursor-pointer self-start"
                  >
                    开始阅读
                    <i className="ri-play-circle-line text-lg"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50" aria-labelledby="chapters-title">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <h2 id="chapters-title" className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
                章节列表
              </h2>
              <div className="bg-background-100 rounded-xl overflow-hidden">
                {comic.chapters.map((ch, idx) => (
                  <div
                    key={ch.num}
                    className={`flex items-center justify-between p-4 transition-colors hover:bg-background-200/70 cursor-pointer ${
                      idx < comic.chapters.length - 1 ? 'border-b border-background-200/70' : ''
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <span className="w-10 h-10 rounded-full bg-background-200 flex items-center justify-center text-sm font-semibold text-foreground-600 flex-shrink-0">
                        {comic.chapters.length - idx}
                      </span>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">第 {ch.num} 话</p>
                        <p className="text-xs text-foreground-500 mt-0.5">{ch.title}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-xs text-foreground-400">{ch.date}</span>
                      <i className="ri-arrow-right-s-line text-foreground-300"></i>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {relatedComics.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100" aria-labelledby="related-title">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-7xl mx-auto">
                <h2 id="related-title" className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                  {relatedComics.map((rc) => (
                    <Link
                      key={rc.id}
                      to={`/comic/${rc.id}`}
                      className="group bg-background-50 rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                    >
                      <div className="aspect-[3/4] overflow-hidden">
                        <img
                          src={rc.cover}
                          alt={rc.title}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-3">
                        <h3 className="font-sans text-sm font-semibold text-foreground-900 line-clamp-1 group-hover:text-primary-600 transition-colors">
                          {rc.title}
                        </h3>
                        <div className="flex items-center gap-1 mt-1.5">
                          <i className="ri-star-fill text-yellow-400 text-xs"></i>
                          <span className="text-xs text-foreground-600">{rc.rating}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}